/**
 * Back-office — Rendez-vous (permission planning.manage).
 * BCL propose 2 créneaux au client puis, une fois confirmé, attribue le RDV à
 * un technicien ou un partenaire. Un refus renvoie le RDV ici en priorité.
 */
import type { EnrichedAppointment } from '@/features/appointments/appointments-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listTechnicians } from '@/features/admin/admin-api';
import {
  appointmentStatusBadge,
  formatSlot,
} from '@/features/appointments/appointment-labels';
import {
  assignAppointment,
  listAllAppointments,
  proposeSlots,
} from '@/features/appointments/appointments-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { listPartners } from '@/features/partners/partners-api';

export function AdminAppointmentsPage() {
  const qc = useQueryClient();
  const appointments = useQuery({
    queryKey: ['admin', 'appointments'],
    queryFn: () => listAllAppointments(),
  });
  const technicians = useQuery({ queryKey: ['admin', 'technicians'], queryFn: listTechnicians });
  const partners = useQuery({ queryKey: ['admin', 'partners', 'assignable'], queryFn: listPartners });

  const [proposeFor, setProposeFor] = useState<EnrichedAppointment | null>(null);
  const [slot1, setSlot1] = useState('');
  const [slot2, setSlot2] = useState('');
  const [assignFor, setAssignFor] = useState<EnrichedAppointment | null>(null);
  const [assigneeId, setAssigneeId] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'appointments'] });

  const proposeMut = useMutation({
    mutationFn: () => {
      const slots = [slot1, slot2]
        .filter(Boolean)
        .map((s) => new Date(s).toISOString());
      return proposeSlots(proposeFor!.id, { slots });
    },
    onSuccess: () => {
      toast.success('Créneaux envoyés au client');
      setProposeFor(null);
      setSlot1('');
      setSlot2('');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const assignMut = useMutation({
    mutationFn: () => assignAppointment(assignFor!.id, assigneeId),
    onSuccess: () => {
      toast.success('Rendez-vous attribué');
      setAssignFor(null);
      setAssigneeId('');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function renderActions(rdv: EnrichedAppointment) {
    if (rdv.status === 'demande') {
      return (
        <Button size="sm" variant="outline" onClick={() => setProposeFor(rdv)}>
          Proposer des créneaux
        </Button>
      );
    }
    if (rdv.status === 'confirme' || rdv.status === 'refuse') {
      return (
        <Button
          size="sm"
          className={rdv.status === 'refuse' ? 'bg-red-600 hover:bg-red-700' : undefined}
          onClick={() => setAssignFor(rdv)}
        >
          {rdv.status === 'refuse' ? 'Réattribuer' : 'Attribuer'}
        </Button>
      );
    }
    return <span className="text-xs text-gray-500">—</span>;
  }

  return (
    <DashboardLayout variant="admin" title="Rendez-vous">
      <Card>
        <CardHeader>
          <CardTitle>Rendez-vous</CardTitle>
          <CardDescription>
            {appointments.data ? `${appointments.data.length} rendez-vous` : 'Chargement…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dossier</TableHead>
                <TableHead>Créneau confirmé</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.data?.length ? (
                appointments.data.map((rdv) => (
                  <TableRow key={rdv.id}>
                    <TableCell className="font-medium">
                      {rdv.projectReference ?? '—'}
                      {rdv.status === 'refuse' && rdv.refusalReason && (
                        <span className="block text-xs text-red-600">
                          Refusé : {rdv.refusalReason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatSlot(rdv.confirmedSlot)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={appointmentStatusBadge[rdv.status].cls}>
                        {appointmentStatusBadge[rdv.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{renderActions(rdv)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                    Aucun rendez-vous.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={proposeFor !== null} onOpenChange={(o) => !o && setProposeFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Proposer des créneaux</DialogTitle>
            <DialogDescription>
              Deux créneaux maximum, envoyés au client par email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="slot1">Créneau 1</Label>
            <Input id="slot1" type="datetime-local" value={slot1} onChange={(e) => setSlot1(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="slot2">Créneau 2 (optionnel)</Label>
            <Input id="slot2" type="datetime-local" value={slot2} onChange={(e) => setSlot2(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposeFor(null)}>
              Annuler
            </Button>
            <Button disabled={!slot1 || proposeMut.isPending} onClick={() => proposeMut.mutate()}>
              {proposeMut.isPending ? 'Envoi…' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignFor !== null} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attribuer le rendez-vous</DialogTitle>
            <DialogDescription>
              À un technicien ou un partenaire. Il devra l'accepter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="assignee">Destinataire</Label>
            <select
              id="assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Choisir —</option>
              <optgroup label="Techniciens">
                {technicians.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.firstName} {t.lastName}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Partenaires">
                {partners.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.profile?.companyName ?? `${p.firstName} ${p.lastName}`}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>
              Annuler
            </Button>
            <Button disabled={!assigneeId || assignMut.isPending} onClick={() => assignMut.mutate()}>
              {assignMut.isPending ? 'Attribution…' : 'Attribuer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
